import Vue from 'vue'
import VueRouter from 'vue-router'
import posthog from 'posthog-js'
import { getHomeRouteForLoggedInUser, getUserData, isUserLoggedIn } from '@/auth/utils'
import ability from '@/libs/acl/ability'
// For some reason, using canNavigate doesn't work but using the code directly does.
// import { canNavigate } from '@/libs/acl/routeProtection'

Vue.use(VueRouter)

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/authentication/LoginPage.vue'),
      meta: {
        layout: 'full',
        ressource: 'Public',
        action: 'read',
        redirectIfLoggedIn: true,
      },
    },
    {
      path: '/',
      name: 'home',
      component: () => import('@/views/HomePage.vue'),
      meta: {
        pageTitle: 'home.title',
        ressource: 'Public',
        action: 'read',
        requireLoggedIn: true,
      },
    },
    // Selection
    {
      path: '/community',
      name: 'select-community',
      component: () => import('@/views/authentication/SelectCommunity.vue'),
      meta: {
        layout: 'full',
        ressource: 'Public',
        action: 'read',
        requireLoggedIn: true,
      },
    },
    {
      path: '/service',
      name: 'select-service',
      component: () => import('@/views/authentication/SelectService.vue'),
      meta: {
        layout: 'full',
        ressource: 'Public',
        action: 'read',
        requireLoggedIn: true,
      },
    },
    {
      path: '/transfer/:code',
      name: 'transfer-service',
      component: () => import('@/views/authentication/TransferService.vue'),
      meta: {
        layout: 'full',
        ressource: 'Public',
        action: 'read',
        requireLoggedIn: true,
      },
    },
    {
      path: '/mdt',
      name: 'mdt',
      component: () => import('@/views/mdt/MDT.vue'),
      meta: {
        pageTitle: 'mdt.title',
        ressource: 'Employee',
        action: 'read',
        requireLoggedIn: true,
      },
    },
    // Dispatch
    {
      path: '/employee-management',
      name: 'employee-management',
      component: () => import('@/views/dispatch/employee-management/EmployeeManagement.vue'),
      meta: {
        pageTitle: 'employee-management.title',
        ressource: 'Dispatch',
        action: 'read',
        requireLoggedIn: true,
        showActiveConnections: true,
        showSearch: true,
      },
    },
    {
      path: '/vehicle-management',
      name: 'vehicle-management',
      component: () => import('@/views/dispatch/vehicle-management/VehicleManagement.vue'),
      meta: {
        pageTitle: 'vehicle-management.title',
        ressource: 'Dispatch',
        action: 'read',
        requireLoggedIn: true,
        showActiveConnections: true,
        showSearch: true,
      },
    },
    {
      path: '/dispatch',
      name: 'dispatch',
      component: () => import('@/views/dispatch/dispatch/DispatchPage.vue'),
      meta: {
        pageTitle: 'dispatch.title',
        ressource: 'Dispatch',
        action: 'read',
        requireLoggedIn: true,
        showActiveConnections: true,
        showSearch: true,
      },
    },
    // Admin
    {
      path: '/history/:userId?',
      name: 'history-overview',
      component: () => import('@/views/history/overview/HistoryOverview.vue'),
      meta: {
        pageTitle: 'history.overview.title',
        ressource: 'Admin',
        action: 'read',
        requireLoggedIn: true,
        showRightToolbar: true,
      },
    },
    {
      path: '/admin/dispatch-settings',
      name: 'dispatch-settings',
      component: () => import('@/views/admin/DispatchSettings/DispatchSettings.vue'),
      meta: {
        pageTitle: 'dispatch-settings.title',
        ressource: 'Admin',
        action: 'read',
        requireLoggedIn: true,
      },
    },
    {
      path: '/admin/misc-settings',
      name: 'misc-settings',
      component: () => import('@/views/admin/MiscSettings/MiscSettings.vue'),
      meta: {
        pageTitle: 'misc-settings.title',
        ressource: 'Admin',
        action: 'read',
        requireLoggedIn: true,
      },
    },
    {
      path: '/admin/subscription',
      name: 'service-subscription',
      component: () => import('@/views/admin/Subscription/ServiceSubscription.vue'),
      meta: {
        pageTitle: 'service-subscription.title',
        ressource: 'Admin',
        action: 'read',
        requireLoggedIn: true,
      },
    },
    // Error pages and redirects
    {
      path: '/error-404',
      name: 'error-404',
      component: () => import('@/views/error/ErrorPage404.vue'),
      meta: {
        layout: 'full',
        ressource: 'Public',
        action: 'read',
      },
    },
    {
      path: '*',
      redirect: 'error-404',
    },
  ],
})

router.beforeEach((to, from, next) => {
  posthog.capture('$pageleave')

  const isLoggedIn = isUserLoggedIn()
  const userData = getUserData()

  if (!isLoggedIn && to.matched.some(record => record.meta.requireLoggedIn)) {
    if (from.query.next) {
      return next({ name: 'login', query: { next: from.query.next } })
    }

    return next({ name: 'login' })
  }

  if (!to.matched.some(record => ability.can(record.meta.action || 'read', record.meta.ressource))) {
    // Redirect to login if not logged in
    if (!isLoggedIn) {
      localStorage.clear()

      if (from.query.next) {
        return next({ name: 'login', query: { next: from.query.next } })
      }

      return next({ name: 'login' })
    }

    // If logged in => not authorized
    if (userData && userData.role === 'employee') {
      return next({ name: 'home', query: { next: 'mdt' } })
    }

    return next({ name: 'login' })
  }

  // Redirect if logged in
  if (to.meta.redirectIfLoggedIn && isLoggedIn) {
    if (to.name === 'login' && to.query.next === 'mdt') {
      return next()
    }

    next(getHomeRouteForLoggedInUser(userData ? userData.role : null))
  }

  setTimeout(() => {
    Vue.prototype.store.dispatch('connections/CHANGE_PAGE', {
      pageName: to.name,
    })
  }, 1000)
  return next()
})

// ? For splash screen
// Remove afterEach hook if you are not using splash screen
router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }

  Vue.nextTick(() => {
    posthog.capture('$pageview')
  })
})

export default router
